import type { FC } from "react";
import React, { useCallback, useEffect, useState } from "react";

import { useWindowDimensions } from "@litres-libs/global-hooks";
import { useTranslation } from "@litres-libs/i18n";
import {
  SearchResponseItemTypesEnum,
  type ISearchCollection,
  type ISearchGenreOrTag,
  type ISearchSeries,
} from "@litres-libs/types";
import classnames from "classnames";
import { useAppSelector } from "@litres-core/store";
import {
  Safemode,
  useSafemode,
  ESafeModeStatus,
} from "@litres-modules/safemode";
import { SmartLink } from "@litres-modules/smart-link";
import { SellableItem } from "../SellableItem/SellableItem";
import { UnsoldItem } from "../UnsoldItem/UnsoldItem";

import type { IResultsListProps } from "../types";
import { isAuthor, isSearchArt, isPublishing } from "../types";
import type { ISpotlightClickData } from "../utils";
import styles from "./ResultsList.module.scss";

export const ResultsList: FC<IResultsListProps> = ({
  searchResults,
  searchString,
  resultsListRef,
  isUserAnonymous,
  setLoading,
  saveSearchQuery,
  searchID,
  isDesktop = true,
  onClose,
}) => {
  const [maxItemsCount, setMaxItemsCount] = useState<number>(9);
  const { t } = useTranslation();

  const { height: windowHeight } = useWindowDimensions();
  const [isAdultContent, setAdultContentSign] = useState(false);

  // логика для safemode (используется в headerMobile)
  const { safeModeStatus } = useSafemode();
  const isPDA = useAppSelector(state => state.browser.pda);

  const shouldShowSafeMode =
    !isPDA && (safeModeStatus !== ESafeModeStatus.undefined || isAdultContent);

  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      setAdultContentSign(
        searchResults.some(item => item.instance.is_adult_content) || false
      );
    }
  }, [searchResults]);

  /* eslint-disable no-useless-escape */
  const clearedSearchString = searchString?.replace(
    /[\s\\\/~#$%^&*(),._!@"№;:?=«»'`\[\]{}+\-<>]/g,
    ""
  );

  const itemsHeights: number[] = [];

  useEffect(() => {
    if (searchResults && searchResults.length > itemsHeights.length) {
      setMaxItemsCount(9);
    }
  }, [searchResults]);

  const itemsRefs = new Array(9).fill(
    useCallback(
      (node: Element) => {
        if (node !== null) {
          itemsHeights.push(node.getBoundingClientRect().height);
        }
      },
      [maxItemsCount, searchResults]
    )
  );

  useEffect(() => {
    let availableHeight = Math.round(windowHeight - windowHeight / 4 - 65);
    let newMaxItemsCount = 0;

    for (const height of itemsHeights) {
      if (availableHeight - height > 0) {
        availableHeight = availableHeight - height;
        newMaxItemsCount++;
      } else {
        break;
      }
    }

    if (newMaxItemsCount !== maxItemsCount) {
      setMaxItemsCount(newMaxItemsCount);
    }
  }, [windowHeight, searchResults, maxItemsCount]);

  const handleShowAllClick = (): void => {
    setLoading(true);
  };

  return (
    <ul
      className={classnames(styles.list, { [styles.mobileList]: !isDesktop })}
      ref={resultsListRef}
      data-testid="header__search-result-list"
    >
      <Safemode
        isVisible={!!shouldShowSafeMode}
        analyticsData={{ internalSource: "search" }}
        customClass={classnames({
          [styles.safemodeContainerMobile]: isPDA,
        })}
      />
      {searchResults?.map((item, index) => {
        const eventData: ISpotlightClickData = {
          search_id: searchID,
          query: searchString || "",
          row_number: index,
          type: item.type,
          id: item.instance.id,
        };

        if (index < maxItemsCount && isSearchArt(item)) {
          const {
            id,
            url,
            title,
            persons,
            prices: price,
            is_free: free,
            my_art_status: owned,
            uuid,
            read_percent: percent,
            cover_url: cover,
            cover_ratio: coverRatio,
            library_information: libraryInfo,
            is_available_with_subscription: availableByPromo,
            release_file_id: releaseFile,
          } = item.instance;

          return (
            <SellableItem
              item={item.instance}
              key={id}
              id={id}
              url={url || "/"}
              title={title}
              persons={persons}
              mem={item.type}
              price={price?.final_price || null}
              currency={price?.currency || null}
              promoPrice={price?.discount_price || null}
              isUserAnonymous={isUserAnonymous}
              free={free}
              libraryServiceType={libraryInfo?.biblio_selfservice}
              availableByPromo={availableByPromo}
              owned={owned}
              uuid={uuid}
              percent={percent}
              coverRatio={coverRatio || 1}
              type={item.type}
              cover={cover}
              externalRef={itemsRefs[index]}
              searchQuery={clearedSearchString}
              setLoading={setLoading}
              saveSearchQuery={saveSearchQuery}
              eventData={eventData}
              releaseFile={releaseFile}
              fileId={id}
              fileType={null}
              isDesktop={isDesktop}
            />
          );
        }

        if (index < maxItemsCount && isAuthor(item)) {
          const { id, full_name: title, url, role } = item.instance;

          return (
            <UnsoldItem
              key={id}
              url={url}
              itemType={item.type}
              role={role}
              title={title}
              id={id}
              isUserAnonymous={isUserAnonymous}
              externalRef={itemsRefs[index]}
              searchQuery={clearedSearchString}
              setLoading={setLoading}
              saveSearchQuery={saveSearchQuery}
              eventData={eventData}
              isDesktop={isDesktop}
            />
          );
        }

        if (index < maxItemsCount && isPublishing(item)) {
          const { url, title, id, small_cover_url: smallLogo } = item.instance;

          return (
            <UnsoldItem
              key={id}
              externalRef={itemsRefs[index]}
              url={url}
              itemType={item.type}
              title={title}
              id={id}
              isUserAnonymous={isUserAnonymous}
              searchQuery={clearedSearchString}
              smallLogo={smallLogo}
              setLoading={setLoading}
              saveSearchQuery={saveSearchQuery}
              eventData={eventData}
              isDesktop={isDesktop}
            />
          );
        }

        if (index < maxItemsCount && !isAuthor(item) && !isSearchArt(item)) {
          const { id, url, name, top_arts } = item.instance as
            | ISearchGenreOrTag
            | ISearchCollection
            | ISearchSeries;

          return (
            <UnsoldItem
              key={id}
              url={url}
              itemType={
                (item.type as SearchResponseItemTypesEnum.tag) ||
                SearchResponseItemTypesEnum.collection ||
                SearchResponseItemTypesEnum.series ||
                SearchResponseItemTypesEnum.publisher ||
                SearchResponseItemTypesEnum.genre
              }
              arts={top_arts}
              title={name}
              id={id}
              isUserAnonymous={isUserAnonymous}
              externalRef={itemsRefs[index]}
              searchQuery={clearedSearchString}
              setLoading={setLoading}
              saveSearchQuery={saveSearchQuery}
              eventData={eventData}
              isDesktop={isDesktop}
            />
          );
        }
      })}

      <div className={styles.buttons}>
        {searchResults && searchResults.length > 6 && searchString && (
          <li
            tabIndex={0}
            className={styles.showAll}
            onClick={handleShowAllClick}
          >
            <SmartLink href={`/search/?q=${searchString}`}>
              <span>
                {isDesktop ? t("Показать все результаты") : t("Показать все")}
              </span>
            </SmartLink>
          </li>
        )}

        {!isDesktop && (
          <li tabIndex={0} className={styles.close} onClick={onClose}>
            {t("Закрыть")}
          </li>
        )}
      </div>
    </ul>
  );
};

export default ResultsList;
